<template>
  <div class="modal-backdrop" @click="close">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3>Load Saved Mapping</h3>
        <button class="btn-close" @click="close">&times;</button>
      </div>

      <div class="modal-body">
        <div v-if="loading" class="loading-indicator">
          <div class="spinner"></div>
          <div class="loading-text">Loading saved mappings...</div>
        </div>

        <div v-else-if="!savedMappings.length" class="empty-state">
          <div class="empty-icon">[Empty]</div>
          <div class="empty-text">No saved mappings found</div>
        </div>

        <ul v-else class="mapping-list">
          <li
            v-for="mapping in savedMappings"
            :key="mapping.id"
            class="mapping-item"
            @click="selectMapping(mapping)"
          >
            <div class="mapping-content">
              <div class="mapping-title">{{ mapping.name }}</div>
              <div class="mapping-subtitle">
                Created: {{ formatDate(mapping.created_at) }}
              </div>
            </div>

            <button
              class="btn-icon"
              @click.stop="deleteMapping(mapping)"
              title="Delete mapping"
            >
              <span class="icon-delete">x</span>
            </button>
          </li>
        </ul>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" @click="close">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      loading: true,
      savedMappings: []
    }
  },

  computed: {
    ...mapGetters(['schoolId'])
  },

  created() {
    this.loadSavedMappings();
  },

  methods: {
    loadSavedMappings() {
      this.loading = true;

      this.$store.dispatch('GET_DRIVE_IMPORT_MAPPINGS', {school_id: this.schoolId})
        .then(response => {
          this.savedMappings = response.data;
          this.loading = false;
        })
        .catch(error => {
          this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
            type: 'error',
            message: 'Failed to load saved mappings: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
          });
          this.loading = false;
        });
    },

    selectMapping(mapping) {
      this.$store.dispatch('GET_DRIVE_IMPORT_MAPPING', {school_id: this.schoolId, mapping_id: mapping.id})
        .then(response => {
          this.$emit('load', response.data);
        })
        .catch(error => {
          this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
            type: 'error',
            message: 'Failed to load mapping: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
          });
        });
    },

    deleteMapping(mapping) {
      if (confirm(`Are you sure you want to delete the mapping "${mapping.name}"?`)) {
        this.$store.dispatch('DELETE_DRIVE_IMPORT_MAPPING', {school_id: this.schoolId, mapping_id: mapping.id})
          .then(() => {
            this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
              type: 'success',
              message: 'Mapping deleted successfully'
            });
            this.loadSavedMappings();
          })
          .catch(error => {
            this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
              type: 'error',
              message: 'Failed to delete mapping: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
            });
          });
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    close() {
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 4px;
  width: 600px;
  max-width: 90%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #757575;
}

.modal-body {
  padding: 16px;
  min-height: 200px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1976d2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  margin-top: 16px;
  color: #757575;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.empty-icon {
  font-size: 24px;
  color: #757575;
  margin-bottom: 16px;
}

.empty-text {
  color: #757575;
}

.mapping-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 300px;
  overflow-y: auto;
}

.mapping-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.mapping-item:last-child {
  border-bottom: none;
}

.mapping-item:hover {
  background-color: #f5f5f5;
}

.mapping-content {
  flex: 1;
}

.mapping-title {
  font-weight: 500;
  margin-bottom: 4px;
}

.mapping-subtitle {
  font-size: 12px;
  color: #757575;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.btn-icon:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.icon-delete {
  font-size: 20px;
  color: #f44336;
  font-weight: bold;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #757575;
}

.btn-secondary:hover {
  background-color: #e0e0e0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
